import { useState } from "react";
import { siteInfo } from "@/mocks/site";

const FORM_ID = "dahcgabp14a1h3mr6im0";
const SUBMIT_ADDR = "https://readdy.ai/api/form/dahcgabp14a1h3mr6im0";

interface SubscribeFormProps {
  compact?: boolean;
}

export default function SubscribeForm({ compact = false }: SubscribeFormProps) {
  const [status, setStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [note, setNote] = useState("");

  const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (status === "loading") return;

    const form = event.currentTarget;
    const payload = new FormData(form);

    const honeypot = String(payload.get("company_alt") ?? "").trim();
    if (honeypot) {
      setStatus("success");
      setFormError("");
      setNote("");
      form.reset();
      return;
    }
    payload.delete("company_alt");

    setStatus("loading");
    setFormError("");

    try {
      const body = new URLSearchParams();
      payload.forEach((value, key) => {
        body.append(key, String(value));
      });

      const response = await fetch(SUBMIT_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: { code?: string; message?: string; meta?: { message?: string; detail?: string } } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }

      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || "";
      const isSpam = String(serverMsg).toLowerCase().includes("spam");
      const ok = response.ok && parsed?.code === "OK" && !isSpam;

      if (ok) {
        setStatus("success");
        setFormError("");
        setNote("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          serverMsg && !isSpam
            ? serverMsg
            : "提交没有成功，请检查邮箱格式后重试，或者直接发邮件联系我们。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络似乎不太稳定，提交失败了，请稍后再试一次。");
    }
  };

  return (
    <section
      id="subscribe"
      className={`w-full ${compact ? "bg-background-50" : "bg-accent-950"} py-16 md:py-20 scroll-mt-20`}
    >
      <div className="w-full px-4 md:px-8">
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-12">
          <div className="lg:col-span-5">
            <span
              className={`inline-flex items-center gap-2 text-xs font-semibold tracking-[0.25em] ${
                compact ? "text-primary-600" : "text-secondary-300"
              }`}
            >
              <span className="h-1.5 w-1.5 rounded-full bg-secondary-500" />
              SUBSCRIBE
            </span>
            <h2
              className={`mt-3 font-heading text-2xl font-bold md:text-3xl ${
                compact ? "text-foreground-950" : "text-white"
              }`}
            >
              订阅每周吃瓜盘点和事件串更新
            </h2>
            <p
              className={`mt-4 max-w-md text-sm leading-relaxed ${
                compact ? "text-foreground-600" : "text-background-200"
              }`}
            >
              每周一次汇总，把当周整理好的事件串按分类列清楚，点开就能按顺序追完。也可以在这里留下对整理内容的反馈或线索。
            </p>
            <a
              href={`mailto:${siteInfo.email}`}
              rel="nofollow"
              className={`mt-5 inline-flex items-center gap-2 text-sm ${
                compact ? "text-foreground-700" : "text-secondary-300"
              } hover:underline`}
            >
              <i className="ri-mail-line" />
              {siteInfo.email}
            </a>
          </div>

          <div className="lg:col-span-7">
            <form
              id={FORM_ID}
              data-readdy-form
              onSubmit={handleSubmit}
              className={`rounded-lg border p-5 md:p-7 ${
                compact ? "border-background-200 bg-background-100" : "border-white/15 bg-white/5"
              }`}
            >
              <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                <div>
                  <label
                    htmlFor="subscriber-name"
                    className={`mb-1.5 block text-xs font-medium ${compact ? "text-foreground-700" : "text-background-200"}`}
                  >
                    称呼
                  </label>
                  <input
                    id="subscriber-name"
                    name="nickname"
                    type="text"
                    maxLength={30}
                    placeholder="怎么称呼你"
                    className={`w-full rounded-md border px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200 ${
                      compact
                        ? "border-background-300 bg-background-50 text-foreground-800"
                        : "border-white/20 bg-black/20 text-white placeholder:text-white/40"
                    }`}
                  />
                </div>
                <div>
                  <label
                    htmlFor="subscriber-email"
                    className={`mb-1.5 block text-xs font-medium ${compact ? "text-foreground-700" : "text-background-200"}`}
                  >
                    邮箱 <span className="text-primary-400">*</span>
                  </label>
                  <input
                    id="subscriber-email"
                    name="email"
                    type="email"
                    required
                    placeholder="you@example.com"
                    className={`w-full rounded-md border px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200 ${
                      compact
                        ? "border-background-300 bg-background-50 text-foreground-800"
                        : "border-white/20 bg-black/20 text-white placeholder:text-white/40"
                    }`}
                  />
                </div>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="subscriber-topic"
                  className={`mb-1.5 block text-xs font-medium ${compact ? "text-foreground-700" : "text-background-200"}`}
                >
                  最想追的方向
                </label>
                <select
                  id="subscriber-topic"
                  name="topic"
                  className={`w-full rounded-md border px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200 ${
                    compact
                      ? "border-background-300 bg-background-50 text-foreground-800"
                      : "border-white/20 bg-black/20 text-white"
                  }`}
                >
                  <option value="娱乐八卦">娱乐八卦</option>
                  <option value="影视热剧">影视热剧</option>
                  <option value="音乐现场">音乐现场</option>
                  <option value="综艺观察">综艺观察</option>
                  <option value="网红动态">网红动态</option>
                  <option value="品牌公关">品牌公关</option>
                </select>
              </div>

              <div className="mt-4">
                <label
                  htmlFor="subscriber-note"
                  className={`mb-1.5 block text-xs font-medium ${compact ? "text-foreground-700" : "text-background-200"}`}
                >
                  想说的话 / 线索（选填，最多 500 字）
                </label>
                <textarea
                  id="subscriber-note"
                  name="message"
                  rows={4}
                  maxLength={500}
                  onChange={(e) => setNote(e.target.value)}
                  value={note}
                  placeholder="比如希望整理哪个方向的合集，或者指出某条事件串里需要补充的地方。"
                  className={`w-full resize-none rounded-md border px-3 py-2.5 text-sm outline-none transition-colors focus:border-primary-400 focus:ring-2 focus:ring-primary-200 ${
                    compact
                      ? "border-background-300 bg-background-50 text-foreground-800"
                      : "border-white/20 bg-black/20 text-white placeholder:text-white/40"
                  }`}
                />
                <span className={`mt-1 block text-right text-xs ${compact ? "text-foreground-500" : "text-white/50"}`}>
                  {note.length}/500
                </span>
              </div>

              <input
                type="text"
                name="company_alt"
                tabIndex={-1}
                autoComplete="off"
                aria-hidden="true"
                readOnly
                className="field-note-extra"
              />

              <div className="mt-5 flex flex-col items-start gap-3 sm:flex-row sm:items-center sm:justify-between">
                <button
                  type="submit"
                  disabled={status === "loading"}
                  className={`inline-flex items-center justify-center gap-2 rounded-full px-6 py-3 text-sm font-semibold transition-colors disabled:opacity-60 whitespace-nowrap ${
                    compact
                      ? "bg-primary-500 text-background-50 hover:bg-primary-600"
                      : "bg-primary-500 text-background-50 hover:bg-primary-600"
                  }`}
                >
                  <i className={status === "loading" ? "ri-loader-4-line animate-spin" : "ri-send-plane-line"} />
                  {status === "loading" ? "提交中…" : "订阅并留下反馈"}
                </button>
                <p className={`text-xs ${compact ? "text-foreground-500" : "text-white/60"}`}>
                  只在每周汇总时联系你，不会用于其他用途。
                </p>
              </div>

              {status === "success" && (
                <p className="mt-4 flex items-center gap-2 rounded-md bg-accent-100 px-3 py-2.5 text-sm text-accent-900">
                  <i className="ri-checkbox-circle-line" />
                  收到啦，本周汇总整理好后会发到你的邮箱。
                </p>
              )}
              {status === "error" && formError && (
                <p className="mt-4 flex items-center gap-2 rounded-md bg-primary-100 px-3 py-2.5 text-sm text-primary-900">
                  <i className="ri-error-warning-line" />
                  {formError}
                </p>
              )}
            </form>
          </div>
        </div>
      </div>
    </section>
  );
}